import { debounce } from "lodash-es"
import { RefCallback, useEffect } from "react"
import { useEventCallback } from "src/components/hooks/useEventCallback"
import { create as createStore } from "zustand"

type State = {
  visible: boolean
  header: HTMLDivElement | null
}

const defaultState: State = {
  visible: true,
  header: null,
}

const store = createStore(() => defaultState)

export function useStickyHeaderState() {
  return store()
}

function setVisible(visible: boolean) {
  store.setState({ visible })
}

export function useStickyHeaderProps() {
  const { visible } = useStickyHeaderState()
  const stickyRef: RefCallback<HTMLDivElement> = useEventCallback((headerElement) =>
    store.setState({ header: headerElement }),
  )

  useEffect(() => {
    let lastScrollY = window.scrollY

    const handleScroll = () => {
      const currentScrollY = window.scrollY

      if (currentScrollY <= 0) {
        setVisible(true)
        lastScrollY = currentScrollY
        return
      }

      setVisible(currentScrollY < lastScrollY)

      lastScrollY = currentScrollY
    }

    handleScroll()

    const handleScrollDebounced = debounce(handleScroll, 100, { leading: true, trailing: true })

    window.addEventListener("scroll", handleScrollDebounced, { passive: true })
    return () => window.removeEventListener("scroll", handleScrollDebounced)
  }, [])

  const stickyClassName = [
    "z-sticky-header fixed no-scroll-adjust top-0 transition-transform duration-300 w-full",
    visible ? "translate-y-0" : "-translate-y-full",
  ].join(" ")

  return { stickyRef, stickyClassName }
}
